import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  makeStyles,
  useTheme,
  Paper,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
  Divider,
  Fade,
  LinearProgress,
  Popover,
  Button
} from "@material-ui/core";
import {
  Reply as ReplyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  InsertEmoticon as EmojiIcon,
  Close as CloseIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Send as SendIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  AttachFile as AttachFileIcon,
  Check as CheckIcon
} from '@material-ui/icons';
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MessageSearch from "../MessageSearch";
import MessageEditDeleteModal from "../MessageEditDeleteModal";
import { chatSelectors } from "../../hooks/useChatReducer";
// WaveSurfer será carregado dinamicamente (lazy) apenas quando o usuário interagir
let WaveSurferLib = null; // cache local para evitar múltiplos imports

const useStyles = makeStyles((theme) => ({  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1, // ✅ Usar flex: 1 ao invés de height: 100% para respeitar o header
    backgroundColor: theme.palette.background.default,
    position: "relative", // ✅ Para posicionamento absoluto do input
    minHeight: 0, // ✅ Permitir que flexbox calcule a altura corretamente
  overflowX: 'hidden', // ✅ Prevenir scroll horizontal no container principal
  // ✅ Bloquear gestos horizontais no mobile e preparar safe-area
  touchAction: 'pan-y',
  overscrollBehaviorX: 'contain',
  maxWidth: '100%',
  // ✅ Variável CSS para safe-area inferior (fallback 0px)
  '--safe-area': '0px',
  "@supports (padding-bottom: env(safe-area-inset-bottom))": {
    '--safe-area': 'env(safe-area-inset-bottom)'
  },
  "@supports (padding-bottom: constant(safe-area-inset-bottom))": {
    '--safe-area': 'constant(safe-area-inset-bottom)'
  }
  },messagesContainer: {
    flex: 1,
    overflowY: "auto",
  overflowX: 'hidden', // ✅ Evitar overflow horizontal na lista
  // ✅ Bloquear gestos horizontais
  touchAction: 'pan-y',
  overscrollBehaviorX: 'contain',
    padding: "8px 16px",
    // ✅ Espaço para composer + safe-area no mobile
    paddingBottom: "calc(60px + var(--safe-area))",
    position: "relative",
    backgroundImage: theme.mode === 'light' 
      ? 'url("data:image/svg+xml,%3Csvg width="260" height="260" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f0f0f0" fill-opacity="0.4"%3E%3Ccircle cx="5" cy="5" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
      : 'url("data:image/svg+xml,%3Csvg width="260" height="260" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23333" fill-opacity="0.4"%3E%3Ccircle cx="5" cy="5" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    ...theme.scrollbarStyles,
    transition: 'padding-bottom 0.3s ease-in-out', // ✅ Transição suave
  },
  // ✅ Classe para quando há typing - adiciona espaço extra
  messagesContainerWithTyping: {
    // ✅ Espaço ajustado para input + typing indicator + safe-area
    paddingBottom: "calc(90px + var(--safe-area))",
  overflowX: 'hidden',
  touchAction: 'pan-y',
  overscrollBehaviorX: 'contain',
  },
  messageGroup: {
    marginBottom: 16,
  },
  messageItem: {
    display: "flex",
    marginBottom: 8,
    alignItems: "flex-end",
  },
  messageItemSent: {
    justifyContent: "flex-end",
  },
  messageItemReceived: {
    justifyContent: "flex-start",
  },
  messageAvatar: {
    width: 32,
    height: 32,
    fontSize: 12,
    marginRight: 8,
    marginBottom: 4,
  },  messageBubble: {
    maxWidth: "70%",
    padding: "8px 12px",
    borderRadius: 16,
    position: "relative",
    wordBreak: "break-word",
    overflowWrap: 'anywhere', // ✅ Quebrar palavras longas/URLs
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    // ✅ Garantir que não há limitações de altura
    minHeight: 'auto',
    maxHeight: 'none',
    overflow: 'visible',
    // ✅ Permitir expansão adequada para textos longos
    width: 'fit-content',
    // ✅ Largura mínima agora limitada ao container (evita overflow em telas estreitas)
    minWidth: 'min(305px, 70%)',
    boxSizing: 'border-box',
  },messageBubbleSent: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottomRightRadius: 4,
    marginLeft: "auto",
    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
    // ✅ Melhor contraste para mensagens enviadas
    '& > .MuiTypography-root': { // Apenas Typography diretos, não os do replyPreview
      color: theme.palette.primary.contrastText,
      fontWeight: 500,
    },
    // ✅ Cores específicas para melhor legibilidade
    background: theme.palette.mode === 'light' 
      ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
      : 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',    // ✅ Correção específica para replyPreview em mensagens enviadas
    '& [class*="replyPreview"]': {
      '& *': { // Força TODOS os elementos filhos
        color: theme.palette.mode === 'light'
          ? '#000000 !important' 
          : 'rgba(255, 255, 255, 0.9) !important',
      },
      '& .MuiTypography-caption': {
        color: theme.palette.mode === 'light' 
          ? '#1976D2 !important' // Nome em azul
          : '#64B5F6 !important',
      },
      '& .MuiTypography-body2': {
        color: theme.palette.mode === 'light'
          ? '#000000 !important' // Conteúdo em preto
          : 'rgba(255, 255, 255, 0.9) !important',
      },
    },
  },messageBubbleReceived: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottomLeftRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    // ✅ Melhor contraste para mensagens recebidas
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
    },
  },
  messageBubbleHighlighted: {
    backgroundColor: theme.palette.warning.light,
    boxShadow: `0 0 10px ${theme.palette.warning.main}`,
    animation: '$highlight 2s ease-in-out',
  },
  '@keyframes highlight': {
    '0%': { backgroundColor: theme.palette.warning.light },
    '50%': { backgroundColor: theme.palette.warning.main },
    '100%': { backgroundColor: theme.palette.warning.light },
  },  messageContent: {
    fontSize: 14,
    lineHeight: 1.4,
    marginBottom: 4,
    // ✅ Melhor contraste para texto em mensagens
    color: 'inherit',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere', // ✅ Quebrar palavras/URLs longas
    whiteSpace: 'pre-wrap',
    // ✅ Garantir que não há limitações de altura ou truncamento
    overflow: 'visible !important', // Forçar visibilidade
    maxHeight: 'none !important', // Remover qualquer limitação de altura
    height: 'auto !important', // Altura automática
    display: 'block !important', // Garantir comportamento de bloco
    textOverflow: 'clip !important', // Não truncar texto
    // ✅ Remover qualquer limitação de WebKit que possa estar escondida
    WebkitLineClamp: 'unset !important',
    WebkitBoxOrient: 'unset !important',
    // ✅ Garantir que o conteúdo não seja cortado por container pai
    minHeight: 'fit-content',
    position: 'relative',
    zIndex: 1,
  },
  messageInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  messageSender: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.primary.main,
    marginBottom: 2,
  },  messageTime: {
    fontSize: 11,
    opacity: 0.8,
    textAlign: "right",
    // ✅ Melhor contraste baseado no tipo de mensagem
    color: 'inherit',
    fontWeight: 400,
  },  inputContainer: {
    position: "absolute", // ✅ FIXO na parte inferior
    bottom: 0,
    left: 0,
    right: 0,
    padding: "8px 12px", // ✅ Padding reduzido pela metade
    // ✅ Garantir espaço acima da barra de gestos no mobile
    paddingBottom: "calc(8px + var(--safe-area))",
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: 6, // ✅ Espaçamento reduzido pela metade
    zIndex: 20, // ✅ Sempre acima de outros elementos
    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)', // ✅ Sombra sutil para elevação
  },  inputRow: {
    display: "flex",
    alignItems: "center", // ✅ Centralizar verticalmente todos os elementos
    gap: 8, // ✅ Espaçamento reduzido
    minHeight: 40, // ✅ Altura reduzida pela metade
    position: 'relative', // ✅ Para posicionamento interno
  },messageInput: {
    flex: 1,    '& .MuiOutlinedInput-root': {
      borderRadius: 28, // ✅ Mais arredondado (WhatsApp-like)
      backgroundColor: theme.palette.background.default,
      minHeight: 40, // ✅ Altura reduzida pela metade
      maxHeight: 120, // ✅ Altura máxima para multiline
      display: 'flex',
      alignItems: 'center',
      paddingRight: '40px', // ✅ Espaço ajustado para o emoji button menor
      '& fieldset': {
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: "8px 12px", // ✅ Padding reduzido pela metade
      fontSize: 14,
      lineHeight: 1.4,
    },
    '& .MuiOutlinedInput-multiline': {
      padding: "8px 12px", // ✅ Padding reduzido pela metade
    },
    '& .MuiInputAdornment-positionEnd': {
      position: 'absolute',
      right: 8, // ✅ Posicionamento mais próximo da borda
      top: '50%',
      transform: 'translateY(-50%)', // ✅ Centralizar verticalmente
      margin: 0, // ✅ Remover margens automáticas
    },
  },  sendButton: {
    width: 40, // ✅ Tamanho reduzido
    height: 40, // ✅ Tamanho reduzido
    borderRadius: '50%', // ✅ Totalmente circular
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 'unset', // ✅ Remover largura mínima padrão
    flexShrink: 0, // ✅ Não permitir encolhimento
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'scale(1.05)', // ✅ Efeito hover sutil
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabled,
      color: theme.palette.text.disabled,
    },
    transition: 'all 0.2s ease', // ✅ Transição suave
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)', // ✅ Sombra para destacar
  },
  actionButton: {
    width: 40, // ✅ Tamanho reduzido
    height: 40, // ✅ Tamanho reduzido
    borderRadius: '50%', // ✅ Totalmente circular
    color: theme.palette.text.secondary,
    minWidth: 'unset', // ✅ Remover largura mínima padrão
    flexShrink: 0, // ✅ Não permitir encolhimento
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
      transform: 'scale(1.05)', // ✅ Efeito hover sutil
    },
    transition: 'all 0.2s ease', // ✅ Transição suave
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: 16,
  },
  dateChip: {
    margin: "16px auto",
    backgroundColor: theme.palette.action.hover,
    fontSize: 12,  },  // ✅ Estilos para mensagem editada
  editedIndicator: {
    fontSize: '10px',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginLeft: 4,
  },
  // ✅ Estilos para modo de edição
  editingMessage: {
    backgroundColor: theme.palette.warning.light + '20',
    border: `1px solid ${theme.palette.warning.main}`,
    position: 'relative',
    '&::before': {
      content: '"✏️"',
      position: 'absolute',
      top: -8,
      left: 8,
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: '10px',
      fontWeight: 'bold',
    }
  },'@keyframes highlight': {
    '0%': { 
      backgroundColor: theme.palette.warning.main,
      transform: 'scale(1.05)',
      boxShadow: `0 0 15px ${theme.palette.warning.main}60`,
    },
    '25%': { 
      transform: 'scale(1.02)',
      boxShadow: `0 0 10px ${theme.palette.warning.main}40`,
    },
    '50%': { 
      backgroundColor: `${theme.palette.warning.light}60`,
      transform: 'scale(1.01)',
    },
    '100%': { 
      backgroundColor: `${theme.palette.warning.light}20`,
      transform: 'scale(1)',
      boxShadow: `0 0 5px ${theme.palette.warning.main}20`,
    },  },  // ✅ Estilos para upload de arquivos - Melhorados
  fileUploadArea: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 12, // ✅ Mais arredondado
    padding: theme.spacing(3), // ✅ Padding maior
    textAlign: 'center',
    backgroundColor: theme.palette.action.hover,
    transition: 'all 0.3s ease',
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
    '&.drag-over': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + '20',
      color: theme.palette.primary.main,
      transform: 'scale(1.02)', // ✅ Efeito visual
      boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
    }
  },
  filePreview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2), // ✅ Espaçamento maior
    padding: theme.spacing(1.5), // ✅ Padding mais generoso
    backgroundColor: theme.palette.action.hover,
    borderRadius: 12, // ✅ Mais arredondado
    margin: theme.spacing(1, 0),
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
      transform: 'translateY(-1px)', // ✅ Efeito hover sutil
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }
  },  // ✅ Container principal da mensagem (inclui balão + reações externas)
  messageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative',
    width: '100%',
  },
  messageWrapperSent: {
    alignItems: 'flex-end',
  },
  
  // ✅ Container do balão + ações (hover)
  messageBubbleWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    position: 'relative',
    maxWidth: '70%',
    // width removido para que o wrapper se ajuste ao conteúdo e permita posicionar botão próximo
    overflow: 'visible', // permitir botão projetar para fora
    '&:hover $messageActions': {
      opacity: 1,
      visibility: 'visible',
    },
    // Estilos de hover para menu removidos
  },
  messageBubbleWrapperSent: {
    flexDirection: 'row-reverse',
  },
  messageMenuButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: -20,
    width: 30,
    height: 30,
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.22)',
    backdropFilter: 'blur(3px)',
    borderRadius: 16,
    opacity: 0,
    transition: 'opacity .18s ease, transform .18s ease',
    zIndex: 4,
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
    border: '1px solid rgba(255,255,255,0.15)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.40)'
    }
  },
  messageMenuButtonReceived: {
    right: 'auto',
    left: -28,
  },
  messageMenuButtonSent: {
    right: -36,
  },
  messageMenuButtonVisible: {
  // Botão de menu removido (substituído por ações inline / long press)
    opacity: 1,
  },
  '@media (hover: none)': {
    messageMenuButton: {
      opacity: 0.9,
  '@media (hover: none)': {
    messageActions: {
      opacity: 1,
      visibility: 'visible'
    }
  },
      transform: 'translateY(-50%)',
    }
  },
  
  // ✅ Ações da mensagem (reação, resposta) - aparecem no hover
  messageActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    alignSelf: 'center', // Centraliza verticalmente em relação ao balão
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 20,
    padding: '2px 4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: `1px solid ${theme.palette.divider}`,
    '& .MuiIconButton-root': {
      width: 24,
      height: 24,
      padding: 2,
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
      }
    }
  },
  
  // ✅ Divisor entre ações do menu
  actionDivider: {
    width: 1,
    height: 16,
    backgroundColor: theme.palette.divider,
    margin: '0 4px',
  },
  
  // ✅ Picker de emojis - posicionado fora do balão
  emojiPicker: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 20,
    padding: '6px 8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: 4,
    zIndex: 10,
    top: -40,
    left: 0,
    animation: '$fadeInUp 0.2s ease',
  },  emojiPickerSent: {
    right: 0,
    left: 'auto',
  },
  
  // ✅ Emoji picker para input de mensagem
  inputEmojiPicker: {
    position: 'absolute',
    bottom: '100%',
    right: 8,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 20,
    padding: '8px 12px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    zIndex: 30,
    marginBottom: 8,
    animation: '$fadeInUp 0.2s ease',
    maxWidth: 300,
    '& .MuiIconButton-root': {
      fontSize: 20,
      width: 32,
      height: 32,
      padding: 4,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'scale(1.1)',
      },
    },
  },
  
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  
  // ✅ Reações externas - fora do balão
  externalReactions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
    marginLeft: 8, // Alinhamento com o balão
    maxWidth: '70%',
  },
  externalReactionsSent: {
    marginLeft: 0,
    marginRight: 8,
    justifyContent: 'flex-end',
  },reactionChip: {
    height: 24,
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'scale(1.05)',
    },
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 'bold',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    }
  },  // ✅ Estilos melhorados para mensagem citada (ambos os temas)
  replyPreview: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(33, 150, 243, 0.12)' // Mais visível no tema claro
      : 'rgba(33, 150, 243, 0.15)',
    borderRadius: '0 8px 8px 0',
    padding: theme.spacing(1),
    border: theme.palette.mode === 'light'
      ? '1px solid rgba(33, 150, 243, 0.2)' // Borda mais visível no claro
      : '1px solid rgba(33, 150, 243, 0.25)',
    // ✅ Sombra sutil para melhor definição em ambos os temas
    boxShadow: theme.palette.mode === 'light'
      ? '0 2px 4px rgba(33, 150, 243, 0.15)' // Sombra azul no tema claro
      : '0 2px 4px rgba(0, 0, 0, 0.25)',    '& .MuiTypography-caption': {
      fontWeight: 600,
      color: theme.palette.mode === 'light' 
        ? '#1976D2 !important' // !important para sobrescrever herança
        : '#64B5F6 !important', // !important para sobrescrever herança
      fontSize: '0.75rem',
      textShadow: theme.palette.mode === 'light'
        ? '0 1px 2px rgba(25, 118, 210, 0.2)' // Sombra azul suave no claro
        : '0 1px 2px rgba(0, 0, 0, 0.5)',
    },    '& .MuiTypography-body2': {
      color: theme.palette.mode === 'light'
        ? '#000000 !important' // !important para garantir texto preto no claro
        : 'rgba(255, 255, 255, 0.9) !important', // !important para tema escuro
      fontStyle: 'italic',
      fontSize: '0.875rem',
      marginTop: '2px',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textShadow: theme.palette.mode === 'light'
        ? 'none' // Sem sombra no tema claro para texto preto
        : '0 1px 2px rgba(0, 0, 0, 0.3)',
      fontWeight: 500, // Texto um pouco mais pesado para melhor legibilidade
    },
    // ✅ Hover effect melhorado para ambos os temas
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(33, 150, 243, 0.18)' // Azul mais intenso no hover (claro)
        : 'rgba(33, 150, 243, 0.22)',
      transform: 'translateX(2px)',
      transition: 'all 0.2s ease-in-out',
      boxShadow: theme.palette.mode === 'light'
        ? '0 3px 6px rgba(33, 150, 243, 0.2)' // Sombra mais intensa no hover (claro)
        : '0 3px 6px rgba(0, 0, 0, 0.3)',
    }
  },
  // ✅ Estilos para status de leitura
  readStatus: {    fontSize: 10,
    color: theme.palette.text.secondary,
    marginLeft: 4,
  },
  typingIndicator: {
    position: "absolute",
    bottom: "80px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",    padding: "10px 16px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "20px",
    border: `1px solid ${theme.palette.divider}`,
    animation: "$typingSlideUp 0.3s ease-out",
    zIndex: 15,
    pointerEvents: "none",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    whiteSpace: "nowrap",
    minWidth: "auto",
    maxWidth: "none",
  },
  typingDots: {
    display: "flex",
    alignItems: "center",
    marginRight: 8,    '& .dot': {
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: theme.palette.mode === 'light' 
        ? theme.palette.primary.main 
        : theme.palette.primary.light,
      margin: "0 2px",
      animation: "$typingPulse 1.4s infinite ease-in-out",
      opacity: 0.8,
      '&:nth-child(1)': {
        animationDelay: '0s',
      },      '&:nth-child(2)': {
        animationDelay: '0.2s',
      },
      '&:nth-child(3)': {
        animationDelay: '0.4s',
      },
    },  },  typingText: {
    color: theme.palette.mode === 'light'
      ? theme.palette.text.primary
      : theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    opacity: 0.9,
    lineHeight: 1,
  },

  
  // ✅ Estilos para interface de gravação de áudio
  recordingInterface: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px', // ✅ Reduzido de 12px 16px para 8px 12px
    backgroundColor: theme.palette.error.light,
    borderRadius: '16px', // ✅ Reduzido de 20px para 16px
    margin: '6px 0', // ✅ Reduzido de 8px para 6px
    gap: 10, // ✅ Reduzido de 12px para 10px
    animation: '$fadeInUp 0.3s ease',
    boxShadow: '0 3px 12px rgba(244, 67, 54, 0.25)', // ✅ Sombra menor
  },
  recordingDots: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '& .dot': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: '#fff',
      animation: '$recordingPulse 1s infinite ease-in-out',
      '&:nth-child(1)': {
        animationDelay: '0s',
      },
      '&:nth-child(2)': {
        animationDelay: '0.2s',
      },
      '&:nth-child(3)': {
        animationDelay: '0.4s',
      },
    },
  },
  recordingTimer: {
    color: '#fff',
    fontWeight: 600,
    fontSize: 16,
    minWidth: 60,
  },  recordingActions: {
    display: 'flex',
    gap: 12,
    marginLeft: 'auto',
    alignItems: 'center',
  },
  recordingButton: {
    width: 48, // ✅ Aumentado de ~32px para 48px
    height: 48,
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#fff',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.3)',
      transform: 'scale(1.05)',
    },
    '&:active': {
      transform: 'scale(0.92)'
    }
  },
  // waveForm estilos
  waveForm: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: 1,
    height: 26, // ✅ Reduzido de 32px para 26px
    justifyContent: 'center',
    '& .wave-bar': {
      width: 3,
      backgroundColor: '#fff',
      borderRadius: 2,
      minHeight: 3, // ✅ Reduzido de 4px para 3px
      animation: '$waveAnimation 0.8s infinite ease-in-out',
      '&:nth-child(1)': { animationDelay: '0s' },
      '&:nth-child(2)': { animationDelay: '0.1s' },
      '&:nth-child(3)': { animationDelay: '0.2s' },
      '&:nth-child(4)': { animationDelay: '0.3s' },
      '&:nth-child(5)': { animationDelay: '0.4s' },
      '&:nth-child(6)': { animationDelay: '0.3s' },
      '&:nth-child(7)': { animationDelay: '0.2s' },
      '&:nth-child(8)': { animationDelay: '0.1s' },
    },
  },

  // ✅ Animações para gravação
  '@keyframes recordingPulse': {
    '0%, 50%': { 
      opacity: 1,
      transform: 'scale(1)',
    },
    '25%': { 
      opacity: 0.5,
      transform: 'scale(0.8)',
    },
  },  '@keyframes waveAnimation': {
    '0%, 100%': { 
      height: 8,
    },
    '50%': { 
      height: 24,
    },
  },
  '@keyframes typingSlideUp': {
    '0%': {
      opacity: 0,
      transform: 'translateX(-50%) translateY(10px) scale(0.9)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(-50%) translateY(0) scale(1)',
    },
  },  '@keyframes typingPulse': {
    '0%, 80%, 100%': {
      opacity: 0.3,
      transform: 'scale(0.8)',
    },
    '40%': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },  // ✅ Animação de pulsação suave para botão de enviar áudio
  '@keyframes pulse': {
    '0%': {
      opacity: 0.8,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
    '100%': {
      opacity: 0.8,
      transform: 'scale(1)',
    },  },
  // ✅ Estilos para player de preview do áudio gravado
  audioPreview: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    margin: '6px 0',
    gap: 12,
    animation: '$fadeInUp 0.3s ease',
    boxShadow: '0 3px 12px rgba(76, 175, 80, 0.25)',
    // ✅ Limitar largura mínima ao espaço disponível
    minWidth: 'min(305px, 100%)',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box', // ✅ Incluir padding no cálculo da largura
  },
  audioPreviewPlayer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  audioPlayButton: {
    width: 36,
    height: 36,
    backgroundColor: '#ffffff',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    }
  },
  audioDuration: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 500,
    minWidth: 40,
  },
  audioWavePreview: {
    flex: 1,
    height: 20,
    backgroundColor: 'rgba(255,255,255,0.3)',
    borderRadius: 10,
    position: 'relative',
    overflow: 'hidden',
  },
  audioProgress: {    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 10,
    transition: 'width 0.1s ease',
  },
}));

// Componente AudioWavePlayer com WaveSurfer.js
function AudioWavePlayer({ src, duration, theme, isSent }) {
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration || 0);
  const [isLoading, setIsLoading] = useState(false); // inicia false até clicar
  const [initError, setInitError] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Formata tempo
  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Destroy on unmount
  useEffect(() => () => { wavesurfer.current && wavesurfer.current.destroy(); }, []);

  const initWaveSurfer = useCallback(async () => {
    if (wavesurfer.current || initError) return; // já inicializado ou falhou
    if (!waveformRef.current) return; // sem container ainda

    // Evitar criar se largura for zero (causa peaks inválidos)
    const containerWidth = waveformRef.current.clientWidth;
    if (!containerWidth || containerWidth < 10) {
      // Tenta novamente no próximo frame
      requestAnimationFrame(initWaveSurfer);
      return;
    }

    try {
      setIsLoading(true);
      if (!WaveSurferLib) {
        const mod = await import(/* webpackChunkName: "wavesurfer-chunk" */ 'wavesurfer.js');
        WaveSurferLib = mod.default || mod;
      }

      const isLightTheme = theme?.palette?.mode === 'light';
      let waveColor, progressColor, cursorColor;
      if (isSent) {
        waveColor = 'rgba(255, 255, 255, 0.4)';
        progressColor = '#ffffff';
        cursorColor = '#ffffff';
      } else {
        waveColor = isLightTheme ? 'rgba(128,128,128,0.5)' : 'rgba(200,200,200,0.4)';
        progressColor = isLightTheme ? '#2196F3' : '#ffffff';
        cursorColor = isLightTheme ? '#0D47A1' : '#1976D2';
      }

      wavesurfer.current = WaveSurferLib.create({
        container: waveformRef.current,
        waveColor,
        progressColor,
        cursorColor,
        barWidth: 1,
        barRadius: 1,
        height: 32,
        normalize: true,
        barGap: 1,
        fillParent: true,
        responsive: true,
        backend: 'WebAudio',
        scrollParent: false
      });

      // Eventos
      wavesurfer.current.on('ready', () => {
        const d = wavesurfer.current.getDuration();
        if (!d || !isFinite(d) || d < 0.01) {
          // duração inválida -> fallback
          setInitError('Duração inválida');
          try { wavesurfer.current.destroy(); } catch(e) {}
          wavesurfer.current = null;
        } else {
          setTotalDuration(d || duration || 0);
        }
        setIsLoading(false);
      });
      wavesurfer.current.on('play', () => setIsPlaying(true));
      wavesurfer.current.on('pause', () => setIsPlaying(false));
      wavesurfer.current.on('audioprocess', () => setCurrentTime(wavesurfer.current.getCurrentTime()));
      wavesurfer.current.on('finish', () => { setIsPlaying(false); setCurrentTime(0); });
      wavesurfer.current.on('error', (e) => { console.warn('WaveSurfer error', e); setInitError('Falha no player'); });

      wavesurfer.current.load(src);
    } catch (err) {
      console.warn('Falha ao inicializar WaveSurfer (fallback simples)', err);
      setInitError('Falha ao inicializar');
      setIsLoading(false);
    }
  }, [src, theme, isSent, duration, initError]);

  const handlePlayPause = async () => {
    setHasInteracted(true);
    if (!wavesurfer.current && !initError) {
      await initWaveSurfer();
    }
    if (wavesurfer.current) {
      wavesurfer.current.playPause();
    }
  };

  // Fallback simples se falhou
  const renderFallback = () => (
    <div style={{ display:'flex', alignItems:'center', gap:8, width:'100%' }}>
      <audio
        src={src}
        onLoadedMetadata={(e) => { if (!totalDuration) setTotalDuration(e.currentTarget.duration); }}
        ref={(el) => { if (el && hasInteracted) { /* pode tocar direto se quiser */ } }}
        style={{ display:'none' }}
      />
      <IconButton size="small" onClick={(e) => { const a = e.currentTarget.previousSibling; if (a && a.paused) { a.play(); } else if (a) { a.pause(); } }}>
        <PlayIcon />
      </IconButton>
      <Typography variant="caption" style={{ fontSize:11 }}>
        {formatTime(currentTime)}/{formatTime(totalDuration)}
      </Typography>
      <Typography variant="caption" color="error" style={{ marginLeft:8 }}>Player simples</Typography>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: 12,
      backgroundColor: 'rgba(33,150,243,0.1)',
      borderRadius: 12,
      width: '100%',
      boxSizing: 'border-box',
      border: '1px solid rgba(33,150,243,0.2)'
    }}>
      <IconButton
        size="small"
        onClick={handlePlayPause}
        disabled={isLoading}
        style={{ color: '#2196F3', padding: 6 }}
      >
        {isLoading ? <CircularProgress size={20} style={{ color: '#2196F3' }} /> : isPlaying ? <PauseIcon /> : <PlayIcon />}
      </IconButton>
      {initError ? (
        renderFallback()
      ) : (
        <div style={{ flex:1, minWidth:0, maxWidth:180, position:'relative', overflow:'hidden' }}>
          <div ref={waveformRef} style={{ width:'100%', maxWidth:'100%', cursor:'pointer', overflow:'hidden' }} />
        </div>
      )}
      <Typography variant="caption" style={{ fontSize:'11px', fontWeight:500, minWidth:35, textAlign:'right', color: theme?.palette?.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)' }}>
        {formatTime(currentTime)}/{formatTime(totalDuration)}
      </Typography>
    </div>
  );
}

const ModernChatMessages = ({
  chat,
  messages = [],
  typingUsers: globalTypingUsers = new Set(), // ✅ Receber typingUsers como prop
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
  isSearchOpen = false,
  onSearchToggle,
  onMessageHighlight,
  highlightedMessageId = null,
  // ✅ Novas props para edição/exclusão
  editingMessage = null,
  onEditMessage,
  onDeleteMessage,
  onSetEditingMessage,
}) => {const classes = useStyles();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
    const baseRef = useRef();
  const messagesEndRef = useRef();
  const messagesContainerRef = useRef();
  const fileInputRef = useRef();
  
  // ✅ Estados para o sistema de edição/exclusão
  const [showEditDeleteModal, setShowEditDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' ou 'delete'
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // ✅ Estados existentes
    const [messageText, setMessageText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Estados para novas funcionalidades
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);  const [dragOver, setDragOver] = useState(false);
  const [reactions, setReactions] = useState({}); // messageId -> reações
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // messageId ou null
  // Removidos: anchorElMessageMenu, menuMessage (menu contextual desativado)
  const [showInputEmojiPicker, setShowInputEmojiPicker] = useState(false); // ✅ Picker para input de mensagem
  const [typingUsers, setTypingUsers] = useState(new Set()); // ✅ Estado local independente
  const [typingTimeout, setTypingTimeout] = useState(null);
    // ✅ Estados para gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioRecorder, setAudioRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  
  // ✅ Estados para player de áudio
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  // ✅ Debug para verificar se mensagens estão sendo atualizadas
  useEffect(() => {
    // console.log(`🔄 ModernChatMessages recebeu ${messages.length} mensagens`);
    // console.log(`🎯 ModernChatMessages typingUsers local:`, typingUsers);
    
    // ✅ Debug específico para verificar estrutura das mensagens
    if (messages.length > 0) {
      /* console.log('📋 Primeiras 3 mensagens para debug:', messages.slice(0, 3).map(msg => ({
        id: msg.id,
        message: msg.message ? msg.message.substring(0, 50) + '...' : 'SEM TEXTO',
        senderId: msg.senderId,
        sender: msg.sender?.name,
        hasMessage: !!msg.message,
        hasMediaPath: !!msg.mediaPath,
        createdAt: msg.createdAt
      }))); */    }
  }, [messages]);

  // ✅ Funções auxiliares definidas antes dos useEffects
  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearReply = () => {
    setReplyToMessage(null);
  };

  // ✅ Sistema de digitação otimizado - definir função antes dos useEffects
  const sendTypingStatus = useCallback(async (isTyping) => {
    try {
      await api.post(`/chats/${chat.id}/typing`, { isTyping });
    } catch (err) {
      // console.error('Erro ao enviar status de digitação:', err);
    }
  }, [chat.id]);

  // ✅ Cleanup do status de digitação quando o chat muda
  useEffect(() => {
    // Limpar campos e status quando o chat muda
    setMessageText("");
    setReplyToMessage(null);
    clearFile();
    setTypingUsers(new Set());
    
    // Parar status de digitação se estiver ativo
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
  sendTypingStatus(false).catch(err => {/* console.warn('Erro ao limpar typing:', err); */});
    }
  }, [chat.id, sendTypingStatus]);
  // ✅ Sistema de digitação otimizado
  useEffect(() => {
    // ✅ Não processar typing durante envio de mensagem
    if (sending) return;
    
    if (messageText && messageText.length > 0) {
      // ✅ Enviar "começou a digitar"
      if (!typingTimeout) {
        sendTypingStatus(true);
      }
      
      // ✅ Limpar timeout anterior
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      
      // ✅ Parar de digitar após 3 segundos sem atividade
      const newTimeout = setTimeout(() => {
        sendTypingStatus(false);
        setTypingTimeout(null);
      }, 3000);
      
      setTypingTimeout(newTimeout);
    } else if (typingTimeout) {
      // ✅ Parou de digitar (campo vazio)
      sendTypingStatus(false);
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [messageText, sendTypingStatus, sending]);
  // ✅ Cleanup no unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        sendTypingStatus(false);
        clearTimeout(typingTimeout);
      }
    };
  }, [sendTypingStatus]);  // ✅ Listener para eventos de typing via Socket (APENAS para o chat atual)
  useEffect(() => {
    if (!user?.id || !user?.companyId || !chat?.id) return;
    
    const companyId = user.companyId;
    const userId = user.id;
    const userChannel = `company-${companyId}-chat-user-${userId}`;
    const chatChannel = `company-${companyId}-chat-${chat.id}`;
    const generalChatChannel = `company-${companyId}-chat`;
    
  const handleTypingEvent = (data) => {      // console.log(`🎯 [ModernChatMessages] TODOS OS EVENTOS recebidos:`, data);
      
      // ✅ Verificar se é evento de typing
      if (data.action !== "user-typing") {
        // console.log(`🚫 [ModernChatMessages] Não é evento de typing, ignorando. Action:`, data.action);
        return;
      }
      
      const { chatId, userId: typingUserId, userName, isTyping } = data;
      
      // ✅ Só processar se for do chat atual E não for o próprio usuário
      if (chatId === chat.id && typingUserId !== user.id) {
        // console.log(`👀 [ModernChatMessages] Processando typing de ${userName} no chat atual`);
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          if (isTyping) {
            newSet.add(userName);
            // console.log(`➕ [ModernChatMessages] Adicionado ${userName}. Total: ${newSet.size}`);
          } else {
            newSet.delete(userName);
            // console.log(`➖ [ModernChatMessages] Removido ${userName}. Total: ${newSet.size}`);
          }
          // console.log(`📝 [ModernChatMessages] Usuários digitando:`, Array.from(newSet));
          return newSet;
        });
        
        // ✅ Limpar automaticamente após 5 segundos
        if (isTyping) {
          setTimeout(() => {
            setTypingUsers(prev => {
              const newSet = new Set(prev);
              newSet.delete(userName);
              // console.log(`⏰ [ModernChatMessages] Auto-removido ${userName} após timeout`);
              return newSet;
            });          }, 5000);
        }
      } else {
        /* console.log(`🚫 [ModernChatMessages] Ignorando evento typing:`, { 
          chatMatch: chatId === chat.id, 
          userMatch: typingUserId !== user.id,
          chatId, 
          currentChatId: chat.id,
          typingUserId,
          currentUserId: user.id
        }); */
      }
    };

    // ✅ Handler para eventos de reação
    const handleReactionEvent = (data) => {
      // console.log(`� [ModernChatMessages] Evento de reação recebido:`, data);
      
      if (data.action === "message-reaction" && data.chatId === chat.id) {
        // console.log(`✅ [ModernChatMessages] Atualizando reações para mensagem ${data.messageId}`);
        
        // ✅ Atualizar estado das reações
        setReactions(prev => ({
          ...prev,
          [data.messageId]: data.reactions || []
        }));
        
        /* console.log(`📊 [ModernChatMessages] Reações atualizadas:`, {
          messageId: data.messageId,
          totalReactions: data.reactions?.length || 0,
          removed: data.removed
        }); */
      }
    };

    // console.log(`�🔌 [ModernChatMessages] Registrando listener para canal: ${userChannel}`);
    // console.log(`🔌 [ModernChatMessages] Registrando listener para canal: ${chatChannel}`);
    // console.log(`🔌 [ModernChatMessages] Registrando listener para canal: ${generalChatChannel}`);    // console.log(`🔌 [ModernChatMessages] Socket conectado:`, socket?.connected);
    // console.log(`🔌 [ModernChatMessages] Dados do usuário:`, { userId, companyId, chatId: chat.id });
    
    socket.on(userChannel, handleTypingEvent);
    socket.on(chatChannel, handleReactionEvent);
    socket.on(generalChatChannel, handleReactionEvent);
    
    // ✅ Monitor geral para DEBUG - escutar TODOS os eventos
    const debugAllEvents = (eventName, ...args) => {
      if (eventName.includes('typing') || eventName.includes('user-typing')) {
        // console.log(`🚨 [DEBUG] Evento relacionado a typing:`, { eventName, args });
      }
    };
    
    socket.onAny && socket.onAny(debugAllEvents);    return () => {
      // console.log(`🔕 [ModernChatMessages] Removendo listener do canal: ${userChannel}`);
      // console.log(`🔕 [ModernChatMessages] Removendo listener do canal: ${chatChannel}`);
      // console.log(`🔕 [ModernChatMessages] Removendo listener do canal: ${generalChatChannel}`);
      socket.off(userChannel, handleTypingEvent);
      socket.off(chatChannel, handleReactionEvent);
      socket.off(generalChatChannel, handleReactionEvent);
      socket.offAny && socket.offAny(debugAllEvents);
    };}, [chat.id, user.id, user.companyId]);

  // ✅ Ajuste do scroll quando typing indicator aparece/desaparece
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    
    if (!messagesContainer) return;
    
    // ✅ Aguardar um momento para o estado se estabilizar
    const timeoutId = setTimeout(() => {
      // ✅ Verificar posição atual após possível mudança
      const currentScrollTop = messagesContainer.scrollTop;
      const currentScrollHeight = messagesContainer.scrollHeight;
      const currentClientHeight = messagesContainer.clientHeight;
      
      // ✅ Verificar se está no final da lista
      const isAtBottom = currentScrollTop + currentClientHeight >= currentScrollHeight - 20;
      
  // console.log(`📊 Scroll state: typing=${typingUsers.size > 0}, top=${currentScrollTop}, height=${currentScrollHeight}, atBottom=${isAtBottom}`);
      
      if (typingUsers.size > 0) {
        // ✅ Quando typing aparece: só scroll para o final se já estava no final
        if (isAtBottom) {
          // console.log(`⬇️ Typing apareceu: mantendo no final`);
          messagesContainer.scrollTop = messagesContainer.scrollHeight - messagesContainer.clientHeight;
        }
        // ✅ Se não estava no final, não faz nada (deixa onde estava)
      } else {
        // ✅ Quando typing desaparece: verificar se precisa ajustar
        // Aguardar um pouco mais para o CSS se estabilizar
        setTimeout(() => {
          const newScrollHeight = messagesContainer.scrollHeight;
          const newClientHeight = messagesContainer.clientHeight;
          const maxScrollTop = newScrollHeight - newClientHeight;
          
          // ✅ Se estava no final, manter no final
          if (isAtBottom && maxScrollTop > 0) {
            // console.log(`⬆️ Typing desapareceu: reposicionando para o final`);
            messagesContainer.scrollTop = maxScrollTop;
          }
          // ✅ Se não estava no final, manter posição atual (não compensar)
          
        }, 150); // ✅ Delay extra para transição CSS completa
      }
    }, 50); // ✅ Delay inicial menor    return () => clearTimeout(timeoutId);
  }, [typingUsers.size]); // ✅ Monitorar mudanças no número de usuários digitando

  // Fechar emoji picker quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showInputEmojiPicker && !event.target.closest('.MuiInputAdornment-root') && !event.target.closest('[class*="inputEmojiPicker"]')) {
        setShowInputEmojiPicker(false);
      }
    };

    if (showInputEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showInputEmojiPicker]);

  // Funções para controle do player de áudio
  const playAudio = () => {
    if (recordedAudio && !isPlaying) {
      const audio = new Audio(URL.createObjectURL(recordedAudio));
      setAudioElement(audio);
      
      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(audio.duration);
      });
      
      audio.addEventListener('timeupdate', () => {
        setAudioCurrentTime(audio.currentTime);
      });
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setAudioCurrentTime(0);
        setAudioElement(null);
      });
      
      audio.play();
      setIsPlaying(true);
    }
  };
  
  const pauseAudio = () => {
    if (audioElement && isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    }
  };
  
  const stopAudio = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setIsPlaying(false);
      setAudioCurrentTime(0);
    }
  };
  // Formatar reações por emoji (otimizado com useCallback)
  // Cache para evitar recomputar reações idênticas
  const reactionsCacheRef = useRef(new Map());
  const formatReactions = useCallback((reactions) => {
  // console.log(`🎭 [formatReactions] Formatando reações:`, reactions);
    
    if (!reactions || reactions.length === 0) {
  // console.log(`🎭 [formatReactions] Nenhuma reação para formatar`);
      return [];
    }

    // Chave baseada em emoji+ids ordenados
    const cacheKey = reactions
      .map(r => `${r.emoji}:${r.user?.id}`)
      .sort()
      .join('|');
    const cached = reactionsCacheRef.current.get(cacheKey);
    if (cached) return cached;
    
    const grouped = reactions.reduce((acc, reaction) => {
      if (!acc[reaction.emoji]) {
        acc[reaction.emoji] = {
          emoji: reaction.emoji,
          count: 0,
          users: [],
          hasUserReacted: false
        };
      }
      
      acc[reaction.emoji].count++;
      acc[reaction.emoji].users.push(reaction.user);
      
      if (reaction.user.id === user.id) {
        acc[reaction.emoji].hasUserReacted = true;
      }
      
      return acc;
    }, {});

    const result = Object.values(grouped);
  // console.log(`🎭 [formatReactions] Resultado:`, result);
    reactionsCacheRef.current.set(cacheKey, result);
    // Limpeza simples se cache crescer demais
    if (reactionsCacheRef.current.size > 500) {
      reactionsCacheRef.current.clear();
    }
    return result;
  }, [user.id]);

  // ✅ Função para verificar se mensagem pode ser editada/deletada
  const canEditOrDeleteMessage = useCallback((message) => {
    if (!message || message.senderId !== user.id) return false;
    
    const messageTime = new Date(message.createdAt);
    const now = new Date();
    const timeDiff = (now - messageTime) / 1000 / 60; // diferença em minutos
    
    return timeDiff <= 5; // 5 minutos limite
  }, [user.id]);

  // (menu contextual removido - usamos apenas ações inline)

  // ✅ Função para abrir modal de edição
  const openEditMessageModal = useCallback((message) => {
    setSelectedMessage(message);
    setModalMode('edit');
    setShowEditDeleteModal(true);
  }, []);

  // ✅ Função para abrir modal de exclusão
  const openDeleteMessageModal = useCallback((message) => {
    setSelectedMessage(message);
    setModalMode('delete');
    setShowEditDeleteModal(true);
  }, []);

  // (handlers de menu removidos)
  // ✅ Handler para edição de mensagem
  const handleEditMessage = useCallback(async (messageId, newText) => {
    setModalLoading(true);
    try {
      await api.put(`/chats/messages/${messageId}`, {
        message: newText
      });
      
      // Callback para o componente pai
      if (onEditMessage) {
        onEditMessage(messageId, newText);
      }
      
      setShowEditDeleteModal(false);
      setSelectedMessage(null);
    } catch (err) {
  // console.error('Erro ao editar mensagem:', err);
      toastError(err);
    } finally {
      setModalLoading(false);
    }
  }, [onEditMessage]);  // ✅ Handler para exclusão de mensagem
  const handleDeleteMessage = useCallback(async (messageId) => {
    setModalLoading(true);
    try {
      await api.delete(`/chats/messages/${messageId}`);
      
      // Callback para o componente pai
      if (onDeleteMessage) {
        onDeleteMessage(messageId);
      }
      
      setShowEditDeleteModal(false);
      setSelectedMessage(null);
    } catch (err) {
  // console.error('Erro ao deletar mensagem:', err);
      toastError(err);
    } finally {
      setModalLoading(false);
    }
  }, [onDeleteMessage]);

  // Otimizar agrupamento de mensagens com useMemo
  const groupedMessages = useMemo(() => {
  // console.log(`📊 Agrupando ${messages.length} mensagens...`);
    
    if (!messages || messages.length === 0) {
  // console.log('⚠️ Nenhuma mensagem para agrupar');
      return [];
    }
    
    const grouped = [];
    let currentGroup = [];
    let currentDate = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.createdAt).toDateString();
      
      if (messageDate !== currentDate) {
        if (currentGroup.length > 0) {
          grouped.push({ type: 'group', messages: currentGroup, date: currentDate });
        }
        currentGroup = [message];
        currentDate = messageDate;
      } else {
        currentGroup.push(message);
      }
    });

    if (currentGroup.length > 0) {
      grouped.push({ type: 'group', messages: currentGroup, date: currentDate });
    }

  // console.log(`📊 Agrupamento concluído: ${grouped.length} grupos com total de ${messages.length} mensagens`);
    grouped.forEach((group, idx) => {
  // console.log(`  📅 Grupo ${idx}: ${group.date} (${group.messages.length} mensagens)`);
    });

    return grouped;
  }, [messages]);

  // =========================
  // Scroll unificado / debounce
  // =========================
  const scrollToBottom = useCallback((behavior = 'auto') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Expor para pai
  useEffect(() => { scrollToBottomRef.current = () => scrollToBottom('smooth'); }, [scrollToBottom]);

  // Debounce simples com requestAnimationFrame + timeout
  const scrollDebounceRef = useRef(null);
  useEffect(() => {
    if (!messages.length) return;
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    scrollDebounceRef.current = setTimeout(() => {
      requestAnimationFrame(() => scrollToBottom('smooth'));
    }, 50);
    return () => scrollDebounceRef.current && clearTimeout(scrollDebounceRef.current);
  }, [messages.length, scrollToBottom]);

  // Highlight scroll centralizado
  useEffect(() => {
    if (!highlightedMessageId || !baseRef.current) return;
    const timer = setTimeout(() => {
      const messageId = String(highlightedMessageId);
      const el = baseRef.current.querySelector(`[data-message-id="${messageId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [highlightedMessageId]);

  // ✅ Timer para gravação de áudio
  useEffect(() => {
    let intervalId;
    
    if (isRecording) {
  // console.log('⏱️ Iniciando timer de gravação');
      intervalId = setInterval(() => {        setRecordingTime(prevTime => {
          const newTime = prevTime + 1;
          // ✅ Limite otimizado de 2 minutos (120 segundos) para melhor UX
          if (newTime >= 120) {
            // console.log('⏰ Limite de 2 minutos atingido, parando gravação');
            handleStopRecording();
            return 120;
          }
          return newTime;
        });
      }, 1000);
    } else {
  // console.log('⏹️ Parando timer de gravação');
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRecording]);

  // ✅ Atalhos de teclado para gravação e preview
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isRecording) {
        if (e.key === 'Enter') {
          e.preventDefault();
          stopAndSendAudio();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          clearRecordedAudio();
        }
      } else if (recordedAudio && !isRecording) {
        // ✅ Atalhos para preview do áudio
        if (e.key === 'Enter') {
          e.preventDefault();
          sendAudio();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          clearRecordedAudio();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isRecording, recordedAudio]);

  // ✅ Funções para novas funcionalidades
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Criar preview para imagens
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };
  const handleReplyTo = (message) => {
    setReplyToMessage(message);
  };

  // ✅ Função para formatar tempo de gravação
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };  // ✅ Funções para gravação de áudio
  const handleStartRecording = async () => {
    try {
  // console.log('🎙️ Solicitando permissão de microfone...');
      
      // ✅ Verificar se o navegador suporta a API
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('API de mídia não suportada neste navegador');
      }
      
      // ✅ Solicitar permissão e obter stream do microfone
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
  // console.log('✅ Permissão concedida, iniciando gravação...');
      
      // ✅ Verificar se MediaRecorder é suportado
      if (!MediaRecorder) {
        throw new Error('MediaRecorder não suportado neste navegador');
      }
        // ✅ Configurar MediaRecorder com otimizações
      let mimeType = 'audio/webm;codecs=opus';
      let recordingOptions = { mimeType };
      
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
        }
      }
      
      // ✅ Configuração otimizada para reduzir tamanho
      recordingOptions = {
        mimeType,
        audioBitsPerSecond: 64000 // 64kbps - reduz 50% do tamanho mantendo qualidade
      };
      
      const mediaRecorder = new MediaRecorder(stream, recordingOptions);
      const audioChunks = [];
      
      // ✅ Coletar dados de áudio
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      
      // ✅ Quando gravação terminar, criar blob
      mediaRecorder.onstop = () => {
        try {
          const audioBlob = new Blob(audioChunks, { type: mimeType });
          
          // ✅ Validação de tamanho (máximo 5MB)
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (audioBlob.size > maxSize) {
            // console.warn('⚠️ Áudio muito grande:', audioBlob.size, 'bytes');
            alert('Áudio muito longo. Tente gravar um áudio menor.');
            setRecordedAudio(null);
          } else if (audioBlob.size < 1000) {
            // console.warn('⚠️ Áudio muito pequeno:', audioBlob.size, 'bytes');
            alert('Áudio muito curto. Grave por pelo menos 1 segundo.');
            setRecordedAudio(null);
          } else {
            setRecordedAudio(audioBlob);
            // console.log('🎵 Áudio gravado:', audioBlob.size, 'bytes');
          }
          
          // ✅ Parar todas as tracks do stream
          stream.getTracks().forEach(track => track.stop());
        } catch (blobError) {
          // console.error('❌ Erro ao criar blob de áudio:', blobError);
        }
      };
      
      // ✅ Iniciar gravação
      mediaRecorder.start();
      setAudioRecorder(mediaRecorder);
      setIsRecording(true);
      setRecordingTime(0);
      
  // console.log('🔴 Gravação iniciada!');
      
    } catch (err) {
  // console.error('❌ Erro ao iniciar gravação:', err);
      
      // ✅ Tratar diferentes tipos de erro
      if (err.name === 'NotAllowedError') {
        alert('⚠️ Permissão de microfone negada. Por favor, permita o acesso ao microfone e tente novamente.');
      } else if (err.name === 'NotFoundError') {
        alert('⚠️ Nenhum microfone encontrado. Verifique se há um microfone conectado.');
      } else if (err.name === 'NotSupportedError') {
        alert('⚠️ Gravação de áudio não suportada neste navegador.');
      } else {
        alert('⚠️ Erro ao acessar o microfone: ' + err.message);
      }
      
      // ✅ Resetar estados em caso de erro
      setIsRecording(false);
      setRecordingTime(0);
      setAudioRecorder(null);
    }
  };

  const handleStopRecording = () => {
    try {
  // console.log('⏹️ Parando gravação de áudio...');
      
      if (audioRecorder && audioRecorder.state === 'recording') {
        audioRecorder.stop();
  // console.log('✅ Gravação parada');
      }
      
      setIsRecording(false);
      
    } catch (err) {
  // console.error('❌ Erro ao parar gravação:', err);
    }
  };
  const clearRecordedAudio = () => {
  // console.log('🗑️ Cancelando gravação...');
    
    // ✅ Parar gravação se estiver ativa
    if (audioRecorder && audioRecorder.state === 'recording') {
      audioRecorder.stop();
    }
    
    // ✅ Parar player se estiver tocando
    stopAudio();
    
    setRecordedAudio(null);
    setIsRecording(false);
    setRecordingTime(0);
    setAudioRecorder(null);
    setAudioDuration(0);
    setAudioCurrentTime(0);
  };

  // ✅ Função para enviar áudio gravado
  const sendAudio = async () => {
    if (!recordedAudio) {
  // console.error('❌ Nenhum áudio para enviar');
      return;
    }

    try {
      setSending(true);
  // console.log('📤 Enviando áudio...', recordedAudio.size, 'bytes');

      // ✅ Criar FormData para envio
      const formData = new FormData();
      
      // ✅ Adicionar arquivo de áudio
      const audioFile = new File([recordedAudio], `audio_${Date.now()}.webm`, {
        type: recordedAudio.type
      });
      formData.append('file', audioFile);
      
      // ✅ Adicionar dados da mensagem
      formData.append('message', ''); // Mensagem vazia para áudio
      if (replyToMessage) {
        formData.append('replyToId', replyToMessage.id.toString());
      }

      // ✅ Enviar para a API
      const response = await api.post(`/chats/${chat.id}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

  // console.log('✅ Áudio enviado com sucesso:', response.data);

      // ✅ Limpar áudio e reply
      clearRecordedAudio();
      setReplyToMessage(null);

      // ✅ Scroll para o final
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }

    } catch (err) {
  // console.error('❌ Erro ao enviar áudio:', err);
      alert('Erro ao enviar áudio: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  // ✅ Função para parar gravação e enviar automaticamente
  const stopAndSendAudio = async () => {
    try {
  // console.log('⏹️ Parando gravação e enviando...');
      
      // ✅ Parar gravação
      if (audioRecorder && audioRecorder.state === 'recording') {
        audioRecorder.stop();
        
        // ✅ Aguardar o blob ser criado e então enviar
        setTimeout(async () => {
          if (recordedAudio) {
            await sendAudio();
          }
        }, 500); // Pequeno delay para garantir que o blob foi criado
      }
      
      setIsRecording(false);
      
    } catch (err) {
  // console.error('❌ Erro ao parar e enviar gravação:', err);
    }
  };
  const toggleReaction = async (messageId, emoji) => {
    try {
  // console.log(`👍 [ModernChatMessages] Enviando reação:`, { messageId, emoji });
      const response = await api.post(`/chats/messages/${messageId}/reactions`, {
        emoji
      });
  // console.log(`✅ [ModernChatMessages] Reação enviada com sucesso:`, response.data);
      // As reações serão atualizadas via socket
    } catch (err) {
  // console.error('❌ [ModernChatMessages] Erro ao reagir à mensagem:', err);
    }
  };

  // ✅ Função para inserir emoji no input de mensagem
  const insertEmojiInInput = (emoji) => {
    setMessageText(prevText => prevText + emoji);
    setShowInputEmojiPicker(false);
  };

  // ✅ Função para marcar mensagem como lida
  const markAsRead = async (messageId) => {
    try {
  // console.log(`👁️ Marcando mensagem ${messageId} como lida...`);
      const response = await api.post(`/chats/messages/${messageId}/read`);
  // console.log(`✅ Mensagem ${messageId} marcada como lida:`, response.data);
      // O status será atualizado via socket
    } catch (err) {
  // console.error(`❌ Erro ao marcar mensagem ${messageId} como lida:`, err);
    }
  };  // ✅ Marcar mensagens como lidas quando entram na viewport (OTIMIZADO)
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    
    // ✅ Marcar como lidas todas as mensagens visíveis que não são do usuário atual
    const markVisibleMessages = () => {
      const unreadMessages = messages.filter(msg => 
        msg.senderId !== user.id && !msg.readAt
      );
      
  // console.log(`👁️ Encontradas ${unreadMessages.length} mensagens não lidas para marcar como lidas`);
      
      // ✅ Marcar uma de cada vez para evitar sobrecarga
      unreadMessages.forEach((message, index) => {
        setTimeout(() => {
          markAsRead(message.id);
        }, index * 100); // ✅ Delay de 100ms entre cada marcação
      });
    };

    // ✅ Aguardar um pouco antes de marcar como lidas (para dar tempo do scroll se estabilizar)
    const timeoutId = setTimeout(markVisibleMessages, 1500); // ✅ Aumentei para 1.5s
    
    return () => clearTimeout(timeoutId);
  }, [messages, user.id]); // ✅ Marcar quando mensagens mudam

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo?.hasMore || loading) return;
    if (scrollTop < 100) {
      handleLoadMore && handleLoadMore();
    }
  };  const handleSend = async () => {
    if (!messageText.trim() && !selectedFile) return;
    
    const tempMessage = messageText.trim();
    const tempFile = selectedFile;
    const tempReply = replyToMessage;
      // ✅ Parar status de digitação ANTES de limpar o texto
    try {
      await sendTypingStatus(false);
    } catch (err) {
  // console.warn('Erro ao limpar status de digitação:', err);
    }
    
    // ✅ Limpar timeout de typing para evitar conflitos
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    
    setSending(true);
      // ✅ Limpar campos imediatamente para melhor UX    
    setMessageText("");
    clearFile();
    clearReply();
    
    try {
      // ✅ Preparar dados da mensagem
      const formData = new FormData();
      
      if (tempMessage) {
        formData.append('message', tempMessage);
      }
      
      if (tempFile) {
        formData.append('file', tempFile);
      }
      
      if (tempReply && tempReply.id) {
        formData.append('replyToId', String(tempReply.id));
      }

      // ✅ Enviar mensagem
      const response = await api.post(`/chats/${chat.id}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });      // ✅ Usar handleSendMessage do componente pai se fornecido
      if (handleSendMessage && response.data.newMessage) {
        handleSendMessage(response.data.newMessage);
      }
      
  // console.log('✅ Mensagem enviada com sucesso');
      
    } catch (err) {
  // console.error('❌ Erro ao enviar mensagem:', err);
      // ✅ Restaurar campos APENAS em caso de erro real
      setMessageText(tempMessage);
      if (tempFile) setSelectedFile(tempFile);
      if (tempReply) setReplyToMessage(tempReply);
      
      // ✅ Mostrar erro para o usuário
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }  };

  // ✅ Emojis disponíveis para reação
  const availableEmojis = ['👍', '❤️', '😂', '😮', '😢', '😡', '👏', '🙏'];

  const getAvatarText = (name) => {
    if (!name) return '?';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const shouldShowAvatar = (message, index) => {
    if (message.senderId === user.id) return false;
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    return !prevMessage || prevMessage.senderId !== message.senderId;
  };

  const shouldShowDateChip = (message, index) => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    const currentDate = new Date(message.createdAt).toDateString();
    const prevDate = new Date(prevMessage.createdAt).toDateString();
    return currentDate !== prevDate;
  };
  const formatDateChip = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: 'long',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };
  const handleRecentSearchClick = (term) => {
    // A busca é controlada pelo componente pai
  // console.log('Busca por termo:', term);
  };

  // Função para destacar mensagem encontrada na busca
  const handleSearchResultClick = (message) => {
    // Chama callback do componente pai para destacar mensagem
    onMessageHighlight && onMessageHighlight(message);
  };
  if (!chat || !chat.id) {
  // console.log('❌ Chat inválido, não renderizando ModernChatMessages');
    return null;
  }

  // console.log(`🎬 Renderizando ModernChatMessages - Chat: ${chat.id}, Mensagens: ${messages.length}, Grupos: ${groupedMessages.length}`);
    return (
    <div className={classes.container}>
      <div 
        ref={messagesContainerRef} // ✅ Ref para controle do scroll
        className={`${classes.messagesContainer} ${typingUsers.size > 0 ? classes.messagesContainerWithTyping : ''}`}
        onScroll={handleScroll}
      >
        {loading && pageInfo?.hasMore && (
          <div className={classes.loadingContainer}>
            <CircularProgress size={20} />
          </div>
        )}        {groupedMessages.map((group, groupIndex) => (
          <div key={groupIndex} className={classes.messageGroup}>
            <Chip 
              label={formatDateChip(group.date)}
              size="small"
              className={classes.dateChip}
            />
            
            {group.messages.map((message, messageIndex) => {
              const isSent = message.senderId === user.id;
              const showAvatar = shouldShowAvatar(message, messageIndex);
              
              // ✅ Debug para verificar renderização
              if (messageIndex === 0) {
                /* console.log(`🎨 Renderizando grupo ${groupIndex}, primeira mensagem:`, {
                  messageId: message.id,
                  hasText: !!message.message,
                  text: message.message ? message.message.substring(0, 30) + '...' : 'SEM TEXTO',
                  senderId: message.senderId,
                  userId: user.id,
                  isSent
                }); */
              }
              
              return (
                <div
                  key={message.id || messageIndex}
                  className={`${classes.messageItem} ${
                    isSent ? classes.messageItemSent : classes.messageItemReceived
                  }`}
                  data-message-id={String(message.id)}
                  data-sender-id={String(message.senderId)}
                >
                  {!isSent && showAvatar && (
                    <Avatar className={classes.messageAvatar}>
                      {getAvatarText(message.sender?.name)}
                    </Avatar>
                  )}
                  
                  {!isSent && !showAvatar && (
                    <div style={{ width: 40 }} />
                  )}

                  {/* ✅ Wrapper da mensagem com reações externas */}
                  <div className={`${classes.messageWrapper} ${isSent ? classes.messageWrapperSent : ''}`}>
                    
                    {/* ✅ Container do balão + ações no hover */}
                    <div
                      className={`${classes.messageBubbleWrapper} ${isSent ? classes.messageBubbleWrapperSent : ''}`}
                      style={message.mediaType === 'audio' ? { maxWidth: '85%' } : undefined}
                    >
                      
                      {/* ✅ Balão da mensagem */}
                      <div
                        className={`${classes.messageBubble} ${
                          isSent ? classes.messageBubbleSent : classes.messageBubbleReceived
                        } ${String(highlightedMessageId) === String(message.id) ? classes.highlightedMessage : ''}`}
                        style={message.mediaType === 'audio' ? { maxWidth: '85%' } : undefined}
                        onMouseEnter={() => setHoveredMessage(message.id)}
                        onMouseLeave={() => setHoveredMessage(null)}
                      >

                        {!isSent && showAvatar && (
                          <Typography className={classes.messageSender}>
                            {message.sender?.name || 'Usuário'}
                          </Typography>
                        )}

                        {/* ✅ Mensagem sendo respondida */}
                        {message.replyTo && (
                          <div 
                            className={classes.replyPreview}
                            style={{
                              backgroundColor: theme.palette.mode === 'light' 
                                ? 'rgba(33, 150, 243, 0.12)' 
                                : 'rgba(33, 150, 243, 0.15)',
                            }}
                          >
                            <Typography 
                              variant="caption"
                              style={{
                                color: theme.palette.mode === 'light' ? '#1976D2 !important' : '#64B5F6 !important',
                                fontWeight: 600,
                                display: 'block',
                                marginBottom: '2px'
                              }}
                            >
                              {message.replyTo.sender?.name || 'Usuário'}
                            </Typography>
                            <div
                              style={{
                                color: theme.palette.mode === 'light' ? '#000000 !important' : 'rgba(255, 255, 255, 0.9) !important',
                                fontStyle: 'italic',
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                fontFamily: theme.typography.fontFamily,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {message.replyTo.message || '📎 Arquivo'}
                            </div>
                          </div>
                        )}
                        {/* ✅ Conteúdo da mensagem */}
                        {message.message && (
                          <div 
                            className={classes.messageContent}
                            onClick={() => {
                              if (message.message.length > 200) {
                                // console.log(`Mensagem completa (${message.message.length} chars):`, message.message);
                              }
                            }}
                            title={message.message.length > 200 ? `Mensagem com ${message.message.length} caracteres - Clique para ver no console` : undefined}
                          >
                            {message.message}
                          </div>
                        )}
                        {/* Botão de menu removido: ações inline assumem funções */}
                        
                        {/* ✅ Debug - caso não tenha mensagem nem mídia */}
                        {!message.message && !message.mediaPath && (
                          <div style={{ color: 'red', fontStyle: 'italic', fontSize: '12px' }}>
                            [DEBUG: Mensagem sem conteúdo - ID: {message.id}]
                          </div>
                        )}                          {/* ✅ Arquivo/Mídia */}
                        {message.mediaPath && (
                          <div style={{ 
                            marginTop: 8,
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box'
                          }}>
                            {message.mediaType === 'image' ? (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/${message.mediaPath}`}
                                alt={message.mediaName}
                                style={{ 
                                  width: '100%', // ✅ Ocupar toda a largura disponível do container
                                  maxHeight: 200, 
                                  borderRadius: 8,
                                  cursor: 'pointer',
                                  objectFit: 'cover', // ✅ Melhor ajuste da imagem
                                  display: 'block' // ✅ Remove espaço em branco abaixo da imagem
                                }}
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/${message.mediaPath}`, '_blank')}
                              />): message.mediaType === 'audio' ? (                              <AudioWavePlayer 
                                src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/${message.mediaPath}`}
                                duration={message.mediaDuration}
                                theme={theme}
                                isSent={isSent}
                              />) : message.mediaType === 'video' ? (
                              <video 
                                controls
                                style={{ 
                                  width: '100%', // ✅ Ocupar toda a largura disponível do container
                                  maxHeight: 300, 
                                  borderRadius: 8,
                                  display: 'block' // ✅ Remove espaço em branco abaixo do vídeo
                                }}
                              >
                                <source 
                                  src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/${message.mediaPath}`}
                                  type="video/mp4" 
                                />
                                Seu navegador não suporta reprodução de vídeo.
                              </video>                            ) : (
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 8,
                                padding: 12,
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: 8,
                                cursor: 'pointer',
                                boxSizing: 'border-box' // ✅ Incluir padding no cálculo da largura
                              }}
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/public/${message.mediaPath}`, '_blank')}
                              >
                                <AttachFileIcon />
                                <Typography variant="body2">
                                  {message.mediaName}
                                </Typography>
                              </div>
                            )}
                          </div>                        )}
                        <div className={classes.messageInfo}>
                          <Typography 
                            className={classes.messageTime}
                            style={{ 
                              color: isSent 
                                ? 'rgba(255,255,255,0.9)' 
                                : theme.palette.text.secondary 
                            }}                          >
                            {formatMessageTime(message.createdAt)}
                            {/* ✅ Indicador de mensagem editada */}
                            {message.isEdited && (
                              <span className={classes.editedIndicator}>
                                (editada)
                              </span>
                            )}
                          </Typography>

                          {/* ✅ Status de leitura para mensagens enviadas */}
                          {isSent && (
                            <Typography 
                              className={classes.readStatus}
                              style={{ 
                                color: message.readAt 
                                  ? 'rgba(255,255,255,0.9)' 
                                  : 'rgba(255,255,255,0.7)',
                                fontSize: 12,
                                marginLeft: 4
                              }}
                            >
                              {message.readAt ? '✓✓' : '✓'}
                            </Typography>
                          )}
                        </div>
                      </div>

                      {/* ✅ Ações da mensagem (aparecem no hover) */}                      <div className={classes.messageActions}>
                        <Tooltip title="Reagir">
                          <IconButton 
                            size="small" 
                            onClick={() => setShowEmojiPicker(
                              showEmojiPicker === message.id ? null : message.id
                            )}
                          >
                            <EmojiIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Responder">
                          <IconButton 
                            size="small"
                            onClick={() => handleReplyTo(message)}
                          >
                            <ReplyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {/* ✅ Divisor e ações de edição/exclusão para mensagens enviadas */}
                        {isSent && canEditOrDeleteMessage && canEditOrDeleteMessage(message) && (
                          <>
                            <div className={classes.actionDivider} />
                            
                            <Tooltip title="Editar mensagem">
                              <IconButton
                                size="small"
                                onClick={() => openEditMessageModal && openEditMessageModal(message)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Deletar mensagem">
                              <IconButton
                                size="small"
                                onClick={() => openDeleteMessageModal && openDeleteMessageModal(message)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </div>

                      {/* ✅ Picker de emojis - fora do balão */}
                      {showEmojiPicker === message.id && (
                        <div className={`${classes.emojiPicker} ${isSent ? classes.emojiPickerSent : ''}`}>
                          {availableEmojis.map(emoji => (
                            <IconButton
                              key={emoji}
                              size="small"
                              onClick={() => {
                                toggleReaction(message.id, emoji);
                                setShowEmojiPicker(null);
                              }}
                              style={{ 
                                fontSize: 18,
                                width: 28,
                                height: 28,
                                padding: 4
                              }}
                            >
                              {emoji}
                            </IconButton>
                          ))}
                        </div>
                      )}
                    </div>                    {/* ✅ Reações externas - fora do balão */}
                    {(() => {
                      // ✅ Usar reações do estado (atualizadas via socket) ou das mensagens (inicial)
                      const messageReactions = reactions[message.id] || message.reactions || [];
                      const formattedReactions = formatReactions(messageReactions);
                      
                      /* console.log(`🎭 [Renderização] Reações para mensagem ${message.id}:`, {
                        fromState: reactions[message.id]?.length || 0,
                        fromMessage: message.reactions?.length || 0,
                        total: messageReactions.length,
                        formatted: formattedReactions.length
                      }); */
                      
                      return formattedReactions.length > 0 && (
                        <div className={`${classes.externalReactions} ${isSent ? classes.externalReactionsSent : ''}`}>
                          {formattedReactions.map((reaction, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={`${reaction.emoji} ${reaction.count}`}
                              className={`${classes.reactionChip} ${
                                reaction.hasUserReacted ? 'active' : ''
                              }`}
                              onClick={() => toggleReaction(message.id, reaction.emoji)}
                            />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>        ))}

        <div ref={messagesEndRef} />
      </div>      {/* ✅ Typing Indicator - FORA do messagesContainer, posição absoluta no container principal */}
      {/* Menu contextual removido */}
      {(() => {
        /* console.log('🎯 [ModernChatMessages] Renderizando typing indicator:', {
          typingUsersSize: typingUsers.size,
          typingUsersArray: Array.from(typingUsers),
          shouldShow: typingUsers.size > 0
        }); */
        return typingUsers.size > 0;
      })() && (
        <div className={classes.typingIndicator}>
          <div className={classes.typingDots}>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
          <div className={classes.typingText}>
            {Array.from(typingUsers).join(', ')} {typingUsers.size === 1 ? 'está' : 'estão'} digitando...
          </div>
        </div>
      )}

{/* Input Container */}
<div className={classes.inputContainer}
  onDrop={handleFileDrop}
  onDragOver={handleDragOver}
  onDragLeave={handleDragLeave}
>
  {/* Reply Preview */}
  {replyToMessage && (
    <div className={classes.replyPreview}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Typography variant="caption" color="textSecondary">
            Respondendo para {replyToMessage.sender?.name || 'Usuário'}
          </Typography>
          <Typography variant="body2">
            {replyToMessage.message || '📎 Arquivo'}
          </Typography>
        </div>
        <IconButton size="small" onClick={clearReply}>×</IconButton>
      </div>
    </div>
  )}

  {/* File Preview */}
  {selectedFile && (
    <div className={classes.filePreview}>
      {filePreview ? (
        <img src={filePreview} alt="Preview"
          style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }}
        />
      ) : (
        <AttachFileIcon />
      )}
      <div style={{ flex: 1 }}>
        <Typography variant="body2">{selectedFile.name}</Typography>
        <Typography variant="caption" color="textSecondary">
          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
        </Typography>
      </div>
      <IconButton size="small" onClick={clearFile}>×</IconButton>
    </div>
  )}

  {/* Drag & Drop Overlay */}
  {dragOver && (
    <div className={`${classes.fileUploadArea} drag-over`}>
      <Typography>Solte o arquivo aqui para enviar</Typography>
    </div>
  )}

  {/* Audio Recording Interface */}
        {isRecording && (
          <div className={classes.recordingInterface}>
            {/* ✅ Indicador visual de gravação */}
            <div className={classes.recordingDots}>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>            {/* ✅ Timer de gravação */}
            <Typography className={classes.recordingTimer}>
              {formatRecordingTime(recordingTime)}
            </Typography>

            {/* ✅ Indicação de atalhos de teclado */}
            <Typography variant="caption" style={{ 
              color: 'rgba(255,255,255,0.8)', 
              fontSize: '10px',
              whiteSpace: 'nowrap',
              marginLeft: 4
            }}>
              Enter: enviar • Esc: cancelar
            </Typography>

            {/* ✅ Forma de onda animada */}
            <div className={classes.waveForm}>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>            </div>

            {/* ✅ Botões de ação */}
            <div className={classes.recordingActions}>
              <Tooltip title="Cancelar gravação">
                <IconButton 
                  className={classes.recordingDeleteButton}
                  onClick={clearRecordedAudio}
                  size="medium"
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
                <Tooltip title="Parar e enviar">
                <IconButton 
                  className={classes.recordingSendButton}
                  onClick={stopAndSendAudio}
                  disabled={sending}
                  size="medium"
                >
                  {sending ? <CircularProgress size={20} /> : <CheckIcon fontSize="medium" />}
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}

  {/* Audio Preview */}
  {recordedAudio && !isRecording && (
    <div className={classes.audioPreview}>
      <div className={classes.audioPreviewPlayer}>
        <Tooltip title={isPlaying ? "Pausar" : "Reproduzir"}>
          <IconButton
            className={classes.audioPlayButton}
            onClick={isPlaying ? pauseAudio : playAudio}
            size="small"
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
        </Tooltip>
        <div className={classes.audioWavePreview}>
          {/* ...waveform e barra de progresso... */}
        </div>
        <Typography className={classes.audioDuration}>
          {formatRecordingTime(Math.floor(audioDuration))}
        </Typography>
      </div>
      <div className={classes.recordingActions}>
        <Tooltip title="Descartar áudio">
          <IconButton
            className={classes.recordingDeleteButton}
            onClick={clearRecordedAudio}
            size="medium"
          >
            <DeleteIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Enviar áudio">
          <IconButton
            className={classes.recordingSendButton}
            onClick={sendAudio}
            disabled={sending}
            size="medium"
          >
            {sending ? <CircularProgress size={20} /> : <SendIcon fontSize="medium" />}
          </IconButton>
        </Tooltip>
      </div>
    </div>
  )}

  {/* Input Row */}
  {!isRecording && (
    <div className={classes.inputRow}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
      />
      <Tooltip title="Anexar arquivo">
        <IconButton
          className={classes.actionButton}
          onClick={() => fileInputRef.current?.click()}
        >
          <AttachFileIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <TextField
        className={classes.messageInput}
        placeholder="Digite uma mensagem..."
        multiline
        maxRows={4}
        value={messageText}
        onChange={(e) => setMessageText(e.target.value)}
        onKeyPress={handleKeyPress}
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Emoji">
                <IconButton
                  className={classes.actionButton}
                  size="small"
                  onClick={() => setShowInputEmojiPicker(!showInputEmojiPicker)}
                  style={{
                    width: 28,
                    height: 28,
                    padding: 4,
                    margin: 0,
                    color: showInputEmojiPicker ? theme.palette.primary.main : 'inherit'
                  }}
                >
                  <EmojiIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isRecording ? "Parar gravação" : "Gravar áudio"}>
                <IconButton
                  className={classes.actionButton}
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  style={{
                    color: isRecording ? '#f44336' : 'inherit',
                    backgroundColor: isRecording ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
                    marginRight: 8,
                  }}
                >
                  {isRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <Tooltip title={(!messageText.trim() && !selectedFile) ? "Digite uma mensagem" : "Enviar"}>
        <span>
          <IconButton
            className={classes.sendButton}
            onClick={handleSend}
            disabled={(!messageText.trim() && !selectedFile) || sending}
          >
            {sending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  )}

  {/* Emoji Picker */}
  {showInputEmojiPicker && !isRecording && (
    <div className={classes.inputEmojiPicker}>
      {availableEmojis.map(emoji => (
        <IconButton
          key={emoji}
          size="small"
          onClick={() => insertEmojiInInput(emoji)}
          style={{ fontSize: 20, width: 32, height: 32, padding: 4 }}
        >
          {emoji}
        </IconButton>
      ))}
    </div>
  )}
</div>

      {/* ✅ Modal para edição/exclusão de mensagens */}
      <MessageEditDeleteModal
        open={showEditDeleteModal}
        onClose={() => setShowEditDeleteModal(false)}
        onEdit={handleEditMessage}
        onDelete={handleDeleteMessage}
        message={selectedMessage}
        mode={modalMode}
        loading={modalLoading}
      />
    </div>
  );
};

export default ModernChatMessages;